/**
 * PackageInfoPanel.tsx - Display package metadata and dependencies
 * 
 * CHANGE HISTORY:
 * - 251031_1436 [pending] danielbeer: Initial creation for package metadata display
 */

import React from 'react';
import './PackageInfoPanel.css';

export interface PackageInfo {
  name: string;
  version: string;
  displayVersion?: string;
  description: string;
  status?: string;
  implementationProgress?: number;
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
  keywords?: string[];
  author?: string;
  license?: string;
}

export interface PackageInfoPanelProps {
  packageInfo: PackageInfo;
  onPackageClick?: (packageName: string) => void;
}

export const PackageInfoPanel: React.FC<PackageInfoPanelProps> = ({ 
  packageInfo, 
  onPackageClick 
}) => {
  const {
    name,
    version,
    displayVersion,
    description,
    status,
    implementationProgress,
    dependencies = {},
    devDependencies = {},
    keywords = [],
    author,
    license
  } = packageInfo;

  const handleDependencyClick = (depName: string) => {
    if (onPackageClick && depName.startsWith('@opcentral/')) {
      onPackageClick(depName);
    }
  };

  const opCentralDeps = Object.entries(dependencies).filter(([name]) => 
    name.startsWith('@opcentral/')
  );

  const externalDeps = Object.entries(dependencies).filter(([name]) => 
    !name.startsWith('@opcentral/')
  );

  return (
    <div className="package-info-panel">
      {/* Header */}
      <div className="package-header">
        <h2 className="package-name">{name}</h2>
        <div className="package-badges">
          <span className="badge version-badge">
            v{displayVersion || version}
          </span>
          {status && (
            <span className={`badge status-badge status-${status}`}>
              {status}
            </span>
          )}
          {implementationProgress !== undefined && (
            <span className="badge progress-badge">
              {implementationProgress}% Complete
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      {description && (
        <div className="package-section">
          <p className="package-description">{description}</p>
        </div>
      )}

      {/* Keywords */}
      {keywords.length > 0 && (
        <div className="package-section">
          <h3 className="section-title">Keywords</h3>
          <div className="keyword-list">
            {keywords.map(keyword => (
              <span key={keyword} className="keyword-tag">
                {keyword}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* OpCentral Dependencies */}
      {opCentralDeps.length > 0 && (
        <div className="package-section">
          <h3 className="section-title">
            OpCentral Dependencies ({opCentralDeps.length})
          </h3>
          <div className="dependency-list">
            {opCentralDeps.map(([depName, depVersion]) => (
              <div 
                key={depName} 
                className="dependency-item clickable"
                onClick={() => handleDependencyClick(depName)}
                title="Click to view package info"
              >
                <span className="dependency-name">{depName}</span>
                <span className="dependency-version">{depVersion}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* External Dependencies */}
      {externalDeps.length > 0 && (
        <div className="package-section">
          <h3 className="section-title">
            External Dependencies ({externalDeps.length})
          </h3>
          <div className="dependency-list">
            {externalDeps.map(([depName, depVersion]) => (
              <div key={depName} className="dependency-item">
                <span className="dependency-name">{depName}</span>
                <span className="dependency-version">{depVersion}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dev Dependencies */}
      {Object.keys(devDependencies).length > 0 && (
        <div className="package-section">
          <h3 className="section-title">
            Dev Dependencies ({Object.keys(devDependencies).length})
          </h3>
          <div className="dependency-list collapsed">
            {Object.entries(devDependencies).slice(0, 3).map(([depName, depVersion]) => (
              <div key={depName} className="dependency-item">
                <span className="dependency-name">{depName}</span>
                <span className="dependency-version">{depVersion}</span>
              </div>
            ))}
            {Object.keys(devDependencies).length > 3 && (
              <div className="dependency-item muted">
                ... and {Object.keys(devDependencies).length - 3} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="package-section">
        <h3 className="section-title">Metadata</h3>
        <div className="metadata-grid">
          {author && (
            <div className="metadata-item">
              <span className="metadata-label">Author:</span>
              <span className="metadata-value">{author}</span>
            </div>
          )}
          {license && (
            <div className="metadata-item">
              <span className="metadata-label">License:</span>
              <span className="metadata-value">{license}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageInfoPanel;
